'use client';

import { useDraggable, useDroppable } from '@dnd-kit/core';
import { useCallback } from 'react';
import { useDevice } from '@/app/hooks';
import { cn } from '@/app/utils';
import type { FileItem } from '../types';

// 统一的可拖拽组件，支持可选的接收拖拽功能
export function DraggableItem({
  item,
  children,
  isSelected,
  isDraggingAny,
  droppable = false,
  disabled = false, // 禁用拖拽
}: {
  item: FileItem;
  children: React.ReactNode;
  isSelected?: boolean;
  isDraggingAny?: boolean;
  droppable?: boolean;
  disabled?: boolean;
}) {
  const { supportsHover } = useDevice();

  // 不支持 hover 的设备（手机或平板）通常也没有滚动，拖拽操作可能会跟往下滚动页面的操作冲突，所以禁用拖拽功能
  const isDragDisabled = disabled || !supportsHover;

  // 拖拽功能
  const {
    attributes,
    listeners,
    setNodeRef: setDragNodeRef,
    isDragging: isDraggingFromHook,
  } = useDraggable({
    id: item.id,
    disabled: isDragDisabled, // 使用合并后的禁用状态
    data: {
      type: item.type,
      item: item,
    },
  });

  // 接收拖拽功能 - 始终调用，但 droppable 为 false 时禁用
  const { setNodeRef: setDropNodeRef, isOver } = useDroppable({
    id: item.id,
    data: droppable
      ? {
          type: 'folder',
          item: item,
        }
      : undefined,
    disabled: !droppable || isDragDisabled,
  });

  // 合并 ref（当启用 droppable 时需要合并两个 ref）
  const setNodeRef = useCallback(
    (node: HTMLElement | null) => {
      setDragNodeRef(node);
      if (droppable) {
        setDropNodeRef(node);
      }
    },
    [droppable, setDragNodeRef, setDropNodeRef],
  );

  // 判断是否应该显示拖拽状态
  const shouldShowDragState = isDraggingFromHook || (isSelected && isDraggingAny);

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cn(
        shouldShowDragState ? 'opacity-50' : '',
        droppable && isOver ? 'border-system-blue02' : 'border-transparent',
        droppable ? 'rounded-8 border' : '',
        'transition-all duration-200',
      )}
    >
      {children}
    </div>
  );
}
